import * as THREE from "three";
import { cloneNatureProp } from "./NatureAssets";
import { cloneTownById, cloneTownProp, pickTownTemplate } from "./FantasyTownAssets";

/**
 * Cozy low-poly park props. Built from primitives so they sit in world space
 * with a fixed facing - they never billboard toward the camera.
 * Characters / animals are canvas sprites elsewhere (RS-classic split).
 * When Kenney nature / Fantasy Town assets are loaded, scenery prefers those.
 */

/** Global prop scale so scenery matches chibi dog billboards. */
export const WORLD_PROP_SCALE = 1.52;

/**
 * Height from stage prop root to the walking surface of the deck
 * (makeStage box center 0.36 + half-height 0.14, then prop scale).
 */
export const STAGE_DECK_TOP = 0.5 * WORLD_PROP_SCALE;

const matCache = new Map<number, THREE.MeshLambertMaterial>();
const basicCache = new Map<string, THREE.MeshBasicMaterial>();
const sphereCache = new Map<string, THREE.SphereGeometry>();
const cylinderCache = new Map<string, THREE.CylinderGeometry>();

export function mat(color: number): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color });
    m.userData.shared = true;
    matCache.set(color, m);
  }
  return m;
}

function basicMat(color: number, opts?: { opacity?: number; transparent?: boolean }): THREE.MeshBasicMaterial {
  const key = `${color}:${opts?.opacity ?? 1}:${opts?.transparent ? 1 : 0}`;
  let m = basicCache.get(key);
  if (!m) {
    m = new THREE.MeshBasicMaterial({
      color,
      transparent: opts?.transparent ?? false,
      opacity: opts?.opacity ?? 1,
      depthWrite: !(opts?.transparent),
    });
    m.userData.shared = true;
    basicCache.set(key, m);
  }
  return m;
}

export function sharedSphere(radius: number, w = 6, h = 5): THREE.SphereGeometry {
  const key = `${radius}:${w}:${h}`;
  let g = sphereCache.get(key);
  if (!g) {
    g = new THREE.SphereGeometry(radius, w, h);
    g.userData.shared = true;
    sphereCache.set(key, g);
  }
  return g;
}

export function sharedCylinder(
  top: number,
  bottom: number,
  height: number,
  segments = 6,
): THREE.CylinderGeometry {
  const key = `${top}:${bottom}:${height}:${segments}`;
  let g = cylinderCache.get(key);
  if (!g) {
    g = new THREE.CylinderGeometry(top, bottom, height, segments);
    g.userData.shared = true;
    cylinderCache.set(key, g);
  }
  return g;
}

function seed(x: number, z: number): number {
  const n = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

export function makeTree(variant = 0): THREE.Group {
  const kenney = cloneNatureProp("tree", variant) ?? cloneNatureProp("pine", variant);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const trunkH = variant === 1 ? 0.85 : 1.05;
  const trunk = new THREE.Mesh(sharedCylinder(0.08, 0.13, trunkH, 6), mat(0x8d6e63));
  trunk.position.y = trunkH * 0.5;
  group.add(trunk);

  const leafA = variant === 1 ? 0x96d068 : 0x86c858;
  const leafB = variant === 1 ? 0xa8dc78 : 0x6eb048;
  const a = new THREE.Mesh(sharedSphere(variant === 1 ? 0.48 : 0.55, 7, 5), mat(leafA));
  a.position.y = variant === 1 ? 1.35 : 1.55;
  a.scale.set(1.1, 0.95, 1.1);
  const b = new THREE.Mesh(sharedSphere(variant === 1 ? 0.32 : 0.36, 6, 5), mat(leafB));
  b.position.set(0.12, variant === 1 ? 1.85 : 2.1, 0.05);
  group.add(a, b);
  return group;
}

export function makeBush(): THREE.Group {
  const kenney = cloneNatureProp("bush", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const a = new THREE.Mesh(sharedSphere(0.28, 6, 5), mat(0x86c858));
  a.position.set(0, 0.24, 0);
  a.scale.set(1.15, 0.9, 1.05);
  const b = new THREE.Mesh(sharedSphere(0.18, 5, 4), mat(0xa0d468));
  b.position.set(0.16, 0.28, 0.08);
  const berry = new THREE.Mesh(sharedSphere(0.05, 5, 4), mat(0xf48fb1));
  berry.position.set(0.08, 0.38, 0.1);
  group.add(a, b, berry);
  return group;
}

export function makeRock(variant = 0): THREE.Group {
  const kenney = cloneNatureProp("rock", variant);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const main = new THREE.Mesh(
    new THREE.DodecahedronGeometry(variant === 1 ? 0.28 : 0.34, 0),
    mat(0x9e958a),
  );
  main.position.y = variant === 1 ? 0.2 : 0.24;
  main.scale.set(1.2, 0.75, 1);
  main.rotation.y = 0.4;
  group.add(main);
  const chip = new THREE.Mesh(new THREE.DodecahedronGeometry(0.14, 0), mat(0xb8b0a4));
  chip.position.set(0.22, 0.12, 0.08);
  chip.scale.set(1, 0.7, 0.9);
  group.add(chip);
  if (variant === 0) {
    const dark = new THREE.Mesh(new THREE.DodecahedronGeometry(0.12, 0), mat(0x7a7368));
    dark.position.set(-0.18, 0.1, -0.1);
    group.add(dark);
  }
  return group;
}

export function makeFence(): THREE.Group {
  const kenney = cloneTownProp("fence", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const postL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.55, 0.08), mat(0x8d6e63));
  postL.position.set(-0.32, 0.28, 0);
  const postR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.55, 0.08), mat(0x8d6e63));
  postR.position.set(0.32, 0.28, 0);
  const rail = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.08, 0.06), mat(0xa1887f));
  rail.position.set(0, 0.38, 0);
  const rail2 = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.08, 0.06), mat(0xa1887f));
  rail2.position.set(0, 0.2, 0);
  group.add(postL, postR, rail, rail2);
  return group;
}

export function makeDigMound(): THREE.Group {
  const group = new THREE.Group();
  const mound = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.55),
    mat(0xd7b48a),
  );
  mound.position.y = 0.06;
  mound.scale.set(1.15, 0.7, 1);
  const hole = new THREE.Mesh(
    new THREE.CircleGeometry(0.12, 12),
    new THREE.MeshLambertMaterial({
      color: 0xc49a6c,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      depthWrite: false,
    }),
  );
  hole.rotation.x = -Math.PI / 2;
  hole.position.y = 0.16;
  const pebble = new THREE.Mesh(new THREE.DodecahedronGeometry(0.06, 0), mat(0xb8b0a4));
  pebble.position.set(0.18, 0.14, 0.1);
  group.add(mound, hole, pebble);
  return group;
}

export function makeFetchBush(): THREE.Group {
  const group = makeBush();
  const stick = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.45, 5), mat(0x4e342e));
  stick.position.set(0.05, 0.34, 0.1);
  stick.rotation.z = 0.9;
  stick.rotation.x = 0.3;
  group.add(stick);
  return group;
}

/** Fallen snack pile - crumbs, berry, little apple on the grass. */
export function makeForageSpot(): THREE.Group {
  const group = new THREE.Group();
  const crumb = new THREE.Mesh(new THREE.DodecahedronGeometry(0.07, 0), mat(0xffe0b2));
  crumb.position.set(-0.08, 0.05, 0.04);
  crumb.scale.set(1.2, 0.55, 1);
  const berry = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), mat(0xe91e63));
  berry.position.set(0.1, 0.06, -0.02);
  const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 4), mat(0x81c784));
  leaf.position.set(0.02, 0.04, 0.12);
  leaf.scale.set(1.4, 0.35, 0.8);
  const apple = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), mat(0xef5350));
  apple.position.set(-0.02, 0.08, -0.1);
  apple.scale.set(1, 0.9, 1);
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.06, 4), mat(0x5d4037));
  stem.position.set(-0.02, 0.16, -0.1);
  group.add(crumb, berry, leaf, apple, stem);
  return group;
}

export function makeForge(): THREE.Group {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.18, 0.55, 8), mat(0xff8a3d));
  body.position.y = 0.3;
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.08, 8), mat(0xb0bec5));
  cap.position.y = 0.6;
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.1, 0.1), mat(0xd84315));
  arm.position.y = 0.38;
  const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.12, 6), mat(0xb0bec5));
  nozzle.position.set(0, 0.66, 0);
  group.add(body, cap, arm, nozzle);
  return group;
}

/** Bank vault - cozy doghouse with a gold bank emblem on the front. */
export function makeVault(): THREE.Group {
  const group = new THREE.Group();
  const wood = mat(0xa1887f);
  const darkWood = mat(0x6d4c41);
  const trim = mat(0x8d6e63);

  const floor = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.08, 0.72), trim);
  floor.position.y = 0.055;

  const back = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.58, 0.06), wood);
  back.position.set(0, 0.37, -0.32);
  const left = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.58, 0.68), wood);
  left.position.set(-0.4, 0.37, 0);
  const right = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.58, 0.68), wood);
  right.position.set(0.4, 0.37, 0);
  // Front flanks leave a dog-sized doorway in the middle.
  const frontL = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.58, 0.06), wood);
  frontL.position.set(-0.3, 0.37, 0.33);
  const frontR = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.58, 0.06), wood);
  frontR.position.set(0.3, 0.37, 0.33);
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.1, 0.06), wood);
  lintel.position.set(0, 0.61, 0.33);

  const roofL = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.07, 0.82), darkWood);
  roofL.position.set(-0.22, 0.78, 0);
  roofL.rotation.z = 0.48;
  const roofR = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.07, 0.82), darkWood);
  roofR.position.set(0.22, 0.78, 0);
  roofR.rotation.z = -0.48;
  const ridge = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.84), trim);
  ridge.position.set(0, 0.92, 0);

  // Bank plaque: cream tile + gold disc + $
  const plaque = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.32, 0.04), mat(0xfff8e1));
  plaque.position.set(0, 0.72, 0.38);
  const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.03, 16), mat(0xf0c24b));
  disc.rotation.x = Math.PI / 2;
  disc.position.set(0, 0.72, 0.41);
  const stem = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.14, 0.02), mat(0xfffde7));
  stem.position.set(0, 0.72, 0.43);
  const topBar = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.028, 0.02), mat(0xfffde7));
  topBar.position.set(0.01, 0.775, 0.43);
  const botBar = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.028, 0.02), mat(0xfffde7));
  botBar.position.set(-0.01, 0.665, 0.43);

  group.add(floor, back, left, right, frontL, frontR, lintel, roofL, roofR, ridge, plaque, disc, stem, topBar, botBar);
  return group;
}

export function makePortal(): THREE.Group {
  // Kept for hospitality portals / props that still want an upright ring.
  const group = new THREE.Group();
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.06, 8, 16), mat(0xffab91));
  ring.position.y = 0.7;
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.7, 6), mat(0x8d6e63));
  post.position.set(-0.38, 0.35, 0);
  const post2 = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.7, 6), mat(0x8d6e63));
  post2.position.set(0.38, 0.35, 0);
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(0.32, 16),
    new THREE.MeshBasicMaterial({
      color: 0xffccbc,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide,
    }),
  );
  glow.position.y = 0.7;
  group.add(ring, post, post2, glow);
  return group;
}

/** Soft disc sprite for portal motes (avoids harsh default point squares). */
let portalMoteTex: THREE.CanvasTexture | null = null;
function portalMoteSprite(): THREE.CanvasTexture {
  if (portalMoteTex) {
    return portalMoteTex;
  }
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const mid = size * 0.5;
  const g = ctx.createRadialGradient(mid, mid, 0, mid, mid, mid);
  // Wider soft core so neighboring motes blend into a continuous ribbon.
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.22, "rgba(230,248,255,0.95)");
  g.addColorStop(0.48, "rgba(180,225,255,0.45)");
  g.addColorStop(0.78, "rgba(140,200,255,0.12)");
  g.addColorStop(1, "rgba(120,180,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  portalMoteTex = new THREE.CanvasTexture(canvas);
  portalMoteTex.colorSpace = THREE.SRGBColorSpace;
  portalMoteTex.needsUpdate = true;
  return portalMoteTex;
}

/** Paint concentric portal rings onto one texture - never stack coplanar meshes. */
function makePortalPadTexture(grand: boolean): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const mid = size * 0.5;
  const ring = (radius: number, fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.arc(mid, mid, radius, 0, Math.PI * 2);
    ctx.fill();
  };
  const stroke = (radius: number, color: string, width: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.arc(mid, mid, radius, 0, Math.PI * 2);
    ctx.stroke();
  };

  ring(mid, grand ? "#9eacb5" : "#b0bec5");
  ring(mid * 0.78, grand ? "#7f919c" : "#90a4ae");
  ring(mid * 0.48, "#7ec8e8");
  stroke(mid * 0.68, "#e8f6fc", size * 0.045);
  stroke(mid * 0.3, "#fff3a8", size * 0.03);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Flat travel pad - cool stone disk with a soft glow and rim particles (no arch).
 * `diameter` is in tile units (matches teleporter w/h).
 * `grand` dresses the plaza hub with a concrete apron + perimeter fountains.
 *
 * Thin plaza inlay (not a pedestal). Concentric colors on ONE textured disc.
 * `walkSurfaces` lifts groundY so paws sit on top instead of sinking through.
 */
export function makeTravelPortal(diameter: number, grand = false): THREE.Group {
  const group = new THREE.Group();
  const r = Math.max(1.35, diameter * 0.46);
  // Thin inlay - just above Floor tiles (portal root sits at terrain height).
  // Keep a clear gap above the apron so the textured disc never z-fights gray bands.
  const apronTop = grand ? 0.045 : 0.038;
  const topY = grand ? 0.082 : 0.06;
  const bodyH = 0.028;
  const y0 = topY - bodyH * 0.5;
  const apronR = r * 2.35;

  if (grand) {
    group.add(makePlazaTravelApron(r, apronTop, apronR));
  }

  // Open-ended shell - sides only; the textured disc is the sole top surface.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 1.02, r * 1.06, bodyH, 32, 1, true),
    mat(grand ? 0x9eacb5 : 0xb0bec5),
  );
  body.position.y = y0;
  body.userData.skipHoverOutline = true;

  const top = new THREE.Mesh(
    new THREE.CircleGeometry(r * 1.02, 48),
    new THREE.MeshBasicMaterial({
      map: makePortalPadTexture(grand),
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  );
  top.rotation.x = -Math.PI / 2;
  top.position.y = topY;
  top.userData.portalPulse = "glow";
  top.userData.skipHoverOutline = true;

  // Slim lip above the disc (kept low so it doesn't clip legs).
  const lipTube = 0.022;
  const lip = new THREE.Mesh(
    new THREE.TorusGeometry(r * 0.98, lipTube, 8, 40),
    new THREE.MeshBasicMaterial({ color: 0xe8f6fc }),
  );
  lip.rotation.x = Math.PI / 2;
  lip.position.y = topY + lipTube * 0.55;
  lip.userData.portalPulse = "ring";

  // Soft additive ribbon under the motes — cheap continuous glow (1 mesh).
  const halo = new THREE.Mesh(
    new THREE.RingGeometry(r * 0.9, r * 1.1, 64),
    new THREE.MeshBasicMaterial({
      color: 0xb3e5fc,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    }),
  );
  halo.rotation.x = -Math.PI / 2;
  halo.position.y = topY + 0.035;
  halo.userData.portalPulse = "halo";
  halo.userData.skipHoverOutline = true;

  // Dense even ring + softer sprites = continuous ribbon without huge budgets.
  const moteCount = grand ? 72 : 56;
  const motePos = new Float32Array(moteCount * 3);
  const motePhases = new Float32Array(moteCount);
  const rimR = r * 0.97;
  for (let i = 0; i < moteCount; i++) {
    const a = (i / moteCount) * Math.PI * 2;
    motePos[i * 3] = Math.cos(a) * rimR;
    motePos[i * 3 + 1] = topY + 0.05;
    motePos[i * 3 + 2] = Math.sin(a) * rimR;
    // Even phase spacing keeps the rim filled as motes rise.
    motePhases[i] = (i / moteCount) * Math.PI * 2;
  }
  const moteGeo = new THREE.BufferGeometry();
  moteGeo.setAttribute("position", new THREE.BufferAttribute(motePos, 3));
  const motes = new THREE.Points(
    moteGeo,
    new THREE.PointsMaterial({
      map: portalMoteSprite(),
      color: 0xe8fbff,
      size: 0.28,
      transparent: true,
      opacity: 0.58,
      depthWrite: false,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
    }),
  );
  motes.userData.portalPulse = "motes";
  motes.userData.moteAnim = {
    rimR,
    topY,
    phases: motePhases,
    spin: 0.55,
    riseH: 0.55,
    riseSpeed: 0.22,
    bob: 0.04,
    linger: 0.42,
  };

  const moteCount2 = grand ? 48 : 36;
  const motePos2 = new Float32Array(moteCount2 * 3);
  const motePhases2 = new Float32Array(moteCount2);
  const rimR2 = r * 1.1;
  for (let i = 0; i < moteCount2; i++) {
    const a = (i / moteCount2) * Math.PI * 2 + Math.PI / moteCount2;
    motePos2[i * 3] = Math.cos(a) * rimR2;
    motePos2[i * 3 + 1] = topY + 0.08;
    motePos2[i * 3 + 2] = Math.sin(a) * rimR2;
    motePhases2[i] = (i / moteCount2) * Math.PI * 2;
  }
  const moteGeo2 = new THREE.BufferGeometry();
  moteGeo2.setAttribute("position", new THREE.BufferAttribute(motePos2, 3));
  const motesOuter = new THREE.Points(
    moteGeo2,
    new THREE.PointsMaterial({
      map: portalMoteSprite(),
      color: 0x90caf9,
      size: 0.18,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
    }),
  );
  motesOuter.userData.portalPulse = "motesOuter";
  motesOuter.userData.moteAnim = {
    rimR: rimR2,
    topY,
    phases: motePhases2,
    spin: -0.38,
    riseH: 0.72,
    riseSpeed: 0.16,
    bob: 0.055,
    linger: 0.38,
  };

  // Warm sparkles that chase the opposite way (reads as a second orbit).
  const sparkCount = grand ? 28 : 20;
  const sparkPos = new Float32Array(sparkCount * 3);
  const sparkPhases = new Float32Array(sparkCount);
  const sparkR = r * 1.03;
  for (let i = 0; i < sparkCount; i++) {
    const a = (i / sparkCount) * Math.PI * 2 + 0.15;
    sparkPos[i * 3] = Math.cos(a) * sparkR;
    sparkPos[i * 3 + 1] = topY + 0.1;
    sparkPos[i * 3 + 2] = Math.sin(a) * sparkR;
    sparkPhases[i] = (i / sparkCount) * Math.PI * 2;
  }
  const sparkGeo = new THREE.BufferGeometry();
  sparkGeo.setAttribute("position", new THREE.BufferAttribute(sparkPos, 3));
  const sparks = new THREE.Points(
    sparkGeo,
    new THREE.PointsMaterial({
      map: portalMoteSprite(),
      color: 0xfff59d,
      size: 0.12,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
    }),
  );
  sparks.userData.portalPulse = "motesSpark";
  sparks.userData.moteAnim = {
    rimR: sparkR,
    topY,
    phases: sparkPhases,
    spin: 0.95,
    riseH: 0.42,
    riseSpeed: 0.48,
    bob: 0.03,
    linger: 0.2,
  };

  // Walkable tops (world = portal.position.y + surfaceY). Inner pad wins via Math.max.
  const walkSurfaces: { radius: number; surfaceY: number }[] = [
    { radius: r * 1.08, surfaceY: topY },
  ];
  if (grand) {
    walkSurfaces.unshift({ radius: apronR * 0.92, surfaceY: apronTop });
  }
  group.userData.travelPortal = true;
  group.userData.walkSurfaces = walkSurfaces;
  group.add(body, top, lip, halo, motes, motesOuter, sparks);

  if (grand) {
    const fountainR = r * 2.05;
    for (let i = 0; i < 4; i++) {
      const a = Math.PI * 0.25 + (i * Math.PI) / 2;
      const fountain = makePlazaFountain();
      fountain.position.set(Math.cos(a) * fountainR, 0, Math.sin(a) * fountainR);
      group.add(fountain);
    }
  }

  return group;
}

/** Wide concrete plaza circle - thin poured inlay, not a raised pedestal. */
function makePlazaTravelApron(padR: number, apronTop: number, apronR: number): THREE.Group {
  const group = new THREE.Group();
  const slabH = 0.04;

  // Annular apron - hole under the portal pad so nothing shares its plane.
  const wellR = padR * 1.08;
  const slab = new THREE.Mesh(
    new THREE.RingGeometry(wellR, apronR, 48),
    mat(0xc5ccd2),
  );
  slab.rotation.x = -Math.PI / 2;
  slab.position.y = apronTop;
  slab.userData.skipHoverOutline = true;

  // Thin side wall so the poured well still reads as a step, not a paper ring.
  const wellWall = new THREE.Mesh(
    new THREE.CylinderGeometry(wellR, wellR * 1.02, slabH, 36, 1, true),
    mat(0xb8c0c6),
  );
  wellWall.position.y = apronTop - slabH * 0.35;
  wellWall.userData.skipHoverOutline = true;

  // Mid band as a ring only (solid disks z-fought the portal disc).
  const mid = new THREE.Mesh(
    new THREE.RingGeometry(padR * 1.28, apronR * 0.74, 48),
    mat(0xb0bac2),
  );
  mid.rotation.x = -Math.PI / 2;
  mid.position.y = apronTop + 0.01;
  mid.userData.skipHoverOutline = true;

  // Outer curb sits at the rim only - mostly outside the walk radius.
  const curbTube = 0.028;
  const curb = new THREE.Mesh(
    new THREE.TorusGeometry(apronR * 0.985, curbTube, 6, 48),
    mat(0x90a0ab),
  );
  curb.rotation.x = Math.PI / 2;
  curb.position.y = apronTop + curbTube * 0.4;
  curb.userData.skipHoverOutline = true;

  const seamInner = padR * 1.28;
  const seamOuter = apronR * 0.94;
  const seamLen = seamOuter - seamInner;
  const seamMid = (seamInner + seamOuter) * 0.5;
  const seamH = 0.016;
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 8;
    const seam = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, seamH, seamLen),
      mat(0x8e9aa4),
    );
    seam.position.set(Math.cos(a) * seamMid, apronTop + seamH * 0.45, Math.sin(a) * seamMid);
    seam.rotation.y = a;
    seam.userData.skipHoverOutline = true;
    group.add(seam);
  }

  // Collar around the portal - ring, not a solid disc under the pad.
  const trim = new THREE.Mesh(
    new THREE.RingGeometry(padR * 1.02, padR * 1.24, 40),
    new THREE.MeshBasicMaterial({ color: 0xd7e8f2 }),
  );
  trim.rotation.x = -Math.PI / 2;
  trim.position.y = apronTop + 0.014;
  trim.userData.skipHoverOutline = true;

  group.add(slab, wellWall, mid, curb, trim);
  return group;
}

/** Petite park fountain for the plaza travel ring. */
function makePlazaFountain(): THREE.Group {
  const group = new THREE.Group();

  const plinth = new THREE.Mesh(
    new THREE.CylinderGeometry(0.42, 0.48, 0.14, 12),
    mat(0x9eabb4),
  );
  plinth.position.y = 0.07;

  const basin = new THREE.Mesh(
    new THREE.CylinderGeometry(0.38, 0.34, 0.16, 14),
    mat(0xb7c4cc),
  );
  basin.position.y = 0.2;

  const basinTopY = basin.position.y + 0.08;
  const water = new THREE.Mesh(
    new THREE.CircleGeometry(0.28, 16),
    new THREE.MeshBasicMaterial({
      color: 0x7ec8e8,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.y = basinTopY + 0.006;
  water.renderOrder = 2;
  water.userData.portalPulse = "fountainWater";

  const spout = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.07, 0.34, 8),
    mat(0x8d9aa3),
  );
  spout.position.y = 0.4;

  const cap = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), mat(0xaab6bf));
  cap.position.y = 0.58;

  const sprayCount = 14;
  const sprayPos = new Float32Array(sprayCount * 3);
  for (let i = 0; i < sprayCount; i++) {
    const a = (i / sprayCount) * Math.PI * 2;
    const lift = 0.55 + (i % 4) * 0.08;
    const out = 0.06 + (i % 3) * 0.04;
    sprayPos[i * 3] = Math.cos(a) * out;
    sprayPos[i * 3 + 1] = lift;
    sprayPos[i * 3 + 2] = Math.sin(a) * out;
  }
  const sprayGeo = new THREE.BufferGeometry();
  sprayGeo.setAttribute("position", new THREE.BufferAttribute(sprayPos, 3));
  const spray = new THREE.Points(
    sprayGeo,
    new THREE.PointsMaterial({
      color: 0xe8f7ff,
      size: 0.09,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      sizeAttenuation: true,
    }),
  );
  spray.userData.portalPulse = "fountainSpray";

  group.add(plinth, basin, water, spout, cap, spray);
  return group;
}

/** Short-lived burst when a pup leaves or arrives via teleporter. */
export function makeTeleportBurst(phase: "leave" | "arrive"): THREE.Group {
  const group = new THREE.Group();
  const count = 28;
  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + (i % 3) * 0.2;
    const r = 0.15 + (i % 5) * 0.04;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = 0.15 + (i % 4) * 0.05;
    positions[i * 3 + 2] = Math.sin(a) * r;
    const up = phase === "leave" ? 1.8 : 1.2;
    velocities[i * 3] = Math.cos(a) * (0.9 + (i % 4) * 0.15);
    velocities[i * 3 + 1] = up + (i % 5) * 0.2;
    velocities[i * 3 + 2] = Math.sin(a) * (0.9 + (i % 4) * 0.15);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const points = new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      color: phase === "leave" ? 0xfff59d : 0x81d4fa,
      size: 0.18,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      sizeAttenuation: true,
    }),
  );
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.35, 0.55, 28),
    new THREE.MeshBasicMaterial({
      color: phase === "leave" ? 0xffe082 : 0xe1f5fe,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.08;

  group.add(points, ring);
  group.userData.teleportBurst = {
    phase,
    started: performance.now(),
    duration: 700,
    velocities,
    points,
    ring,
  };
  return group;
}

/** Sparkle puff at a dig / fetch / forage node when the pup finishes harvesting. */
export function makeHarvestBurst(skillId: "digging" | "fetching" | "sniffing" | string = "digging"): THREE.Group {
  const group = new THREE.Group();
  const count = 22;
  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + (i % 5) * 0.17;
    const r = 0.08 + (i % 4) * 0.05;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = 0.2 + (i % 3) * 0.06;
    positions[i * 3 + 2] = Math.sin(a) * r;
    velocities[i * 3] = Math.cos(a) * (1.1 + (i % 4) * 0.2);
    velocities[i * 3 + 1] = 1.6 + (i % 5) * 0.35;
    velocities[i * 3 + 2] = Math.sin(a) * (1.1 + (i % 4) * 0.2);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const color =
    skillId === "fetching" ? 0xa5d6a7
    : skillId === "sniffing" ? 0xf48fb1
    : 0xffe082;
  const points = new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      color,
      size: 0.16,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      sizeAttenuation: true,
    }),
  );
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.22, 0.48, 24),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.75,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.1;

  group.add(points, ring);
  group.userData.harvestBurst = {
    started: performance.now(),
    duration: 780,
    velocities,
    points,
    ring,
  };
  return group;
}

/** Flat step-on travel pad sized to teleporter footprint (world units ≈ tiles). */
export function makeTravelPad(width: number, depth: number, menu = false): THREE.Group {
  const group = new THREE.Group();
  const w = Math.max(2, width) * 1.08;
  const d = Math.max(2, depth) * 1.08;
  const r = Math.min(w, d) * 0.48;
  const stone = menu ? 0xe8c9a0 : 0xe0c4a8;
  const glow = menu ? 0xffe082 : 0xffc1d6;
  const rim = menu ? 0xffecb3 : 0xf8bbd0;

  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(r, r * 1.04, 0.12, 28),
    new THREE.MeshLambertMaterial({
      color: stone,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
  );
  // Bottom clears Floor / Path so the pad doesn't shimmer.
  base.position.y = 0.08;

  const inset = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 0.82, r * 0.84, 0.04, 28),
    new THREE.MeshBasicMaterial({
      color: glow,
      transparent: true,
      opacity: menu ? 0.72 : 0.55,
      depthWrite: false,
    }),
  );
  inset.position.y = 0.16;

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(r * 0.36, r * 0.62, 28),
    new THREE.MeshBasicMaterial({
      color: rim,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.185;

  // Low posts on a soft circle so the pad reads round, not boxy.
  const postGeo = new THREE.CylinderGeometry(0.08, 0.1, 0.36, 6);
  const postMat = mat(0x8d6e63);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.set(Math.cos(a) * r * 0.78, 0.2, Math.sin(a) * r * 0.78);
    group.add(post);
  }

  group.add(base, inset, ring);
  group.userData.walkSurfaces = [{ radius: r * 1.02, surfaceY: 0.185 }];
  return group;
}

export function makeFlowerPatch(kind = 0): THREE.Group {
  const kenney = cloneNatureProp("flower", kind);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const colors = [0xf8bbd0, 0xffe082, 0xe1bee7, 0xffccbc];
  for (let i = 0; i < 3; i++) {
    const bloom = new THREE.Mesh(
      sharedSphere(0.07, 5, 4),
      mat(colors[(kind + i) % colors.length]!),
    );
    const ox = ((i % 3) - 1) * 0.14;
    const oz = i === 1 ? 0.08 : -0.06;
    bloom.position.set(ox, 0.16, oz);
    group.add(bloom);
  }
  return group;
}

export function makeLampPost(): THREE.Group {
  const kenney = cloneTownProp("lantern", 0);
  if (kenney) {
    // Soft glow so park night/dusk reading stays cozy.
    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(0.32, 10, 8),
      new THREE.MeshBasicMaterial({
        color: 0xffe0b2,
        transparent: true,
        opacity: 0.26,
        depthWrite: false,
      }),
    );
    glow.position.y = 2.75;
    glow.userData.ambientGlow = "lamp";
    kenney.add(glow);
    kenney.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj !== glow) {
        const box = new THREE.Box3().setFromObject(obj);
        if (box.min.y > 2.0) {
          obj.userData.ambientGlow = "lamp";
        }
      }
    });
    return kenney;
  }
  const group = new THREE.Group();
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 2.85, 8), mat(0xcfd8dc));
  pole.position.y = 1.425;
  const lamp = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 10, 8),
    new THREE.MeshBasicMaterial({ color: 0xfff3e0 }),
  );
  lamp.position.y = 2.85;
  lamp.userData.ambientGlow = "lamp";
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.36, 10, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffe0b2,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    }),
  );
  glow.position.y = 2.85;
  glow.userData.ambientGlow = "lamp";
  const hat = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.16, 8), mat(0xbcaaa4));
  hat.position.y = 3.05;
  group.add(pole, glow, lamp, hat);
  return group;
}

export function makeBench(): THREE.Group {
  // Holiday Kit orange park bench (not Retro Urban wood detail-bench).
  const kenney = cloneTownById("park-bench-holiday") ?? cloneTownProp("bench", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const seat = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.08, 0.38), mat(0xe8a05a));
  seat.position.y = 0.4;
  const back = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.36, 0.07), mat(0xd4894a));
  back.position.set(0, 0.62, -0.14);
  const legGeo = new THREE.BoxGeometry(0.07, 0.4, 0.07);
  for (const [x, z] of [
    [-0.42, 0.12],
    [0.42, 0.12],
    [-0.42, -0.12],
    [0.42, -0.12],
  ] as const) {
    const leg = new THREE.Mesh(legGeo.clone(), mat(0xb0bec5));
    leg.position.set(x, 0.2, z);
    group.add(leg);
  }
  group.add(seat, back);
  return group;
}

/** Seat height for park benches (world units, after WORLD_PROP_SCALE). */
export function benchSeatY(): number {
  const template = pickTownTemplate("bench", 0);
  if (template?.seatY != null) {
    return template.seatY * WORLD_PROP_SCALE;
  }
  return 0.4 * WORLD_PROP_SCALE;
}

/** Local Y of the stadium seat plank (before WORLD_PROP_SCALE). */
export const STADIUM_SEAT_LOCAL_Y = 0.28;
/** World seat height for amphitheater sitters. */
export const STADIUM_SEAT_TOP = STADIUM_SEAT_LOCAL_Y * WORLD_PROP_SCALE;

/**
 * Stadium-style bleacher section: concrete riser flush to ground, wide plank,
 * low backrest - for amphitheater tiers (not park picnic benches).
 */
export function makeStadiumSeat(): THREE.Group {
  const group = new THREE.Group();
  const riser = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.2, 0.78), mat(0x9e9e9e));
  riser.position.y = 0.1;
  const lip = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.06, 0.1), mat(0x757575));
  lip.position.set(0, 0.17, 0.34);
  const seat = new THREE.Mesh(new THREE.BoxGeometry(2.28, 0.07, 0.46), mat(0x6d4c41));
  seat.position.set(0, STADIUM_SEAT_LOCAL_Y, 0.06);
  const back = new THREE.Mesh(new THREE.BoxGeometry(2.28, 0.32, 0.07), mat(0x5d4037));
  back.position.set(0, 0.46, -0.18);
  const railGeo = new THREE.BoxGeometry(0.08, 0.42, 0.08);
  for (const x of [-1.08, 1.08] as const) {
    const rail = new THREE.Mesh(railGeo.clone(), mat(0x455a64));
    rail.position.set(x, 0.31, -0.18);
    group.add(rail);
  }
  // Front toe-kick so the unit reads planted on the tier, not hovering.
  const kick = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.08, 0.08), mat(0x616161));
  kick.position.set(0, 0.04, 0.36);
  group.add(riser, lip, seat, back, kick);
  return group;
}

/** Cute dog-park hoop - short pole, soft backboard, tennis-ball-friendly rim. */
export function makeBasketHoop(): THREE.Group {
  const group = new THREE.Group();
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 1.55, 8), mat(0xb0bec5));
  pole.position.y = 0.78;
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.55, 0.06), mat(0xfff8e1));
  board.position.set(0, 1.55, 0.12);
  const square = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.22, 0.02), mat(0xff7043));
  square.position.set(0, 1.48, 0.16);
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.2, 0.03, 8, 16),
    mat(0xff7043),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.set(0, 1.35, 0.32);
  const net = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.1, 0.28, 8, 1, true),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.45, side: THREE.DoubleSide }),
  );
  net.position.set(0, 1.2, 0.32);
  group.add(pole, board, square, rim, net);
  return group;
}

/** Simple tennis net across a court - posts + band + mesh. */
export function makeTennisNet(): THREE.Group {
  const group = new THREE.Group();
  const postGeo = new THREE.CylinderGeometry(0.05, 0.06, 1.05, 6);
  const postMat = mat(0x546e7a);
  const left = new THREE.Mesh(postGeo, postMat);
  left.position.set(-1.35, 0.52, 0);
  const right = new THREE.Mesh(postGeo.clone(), postMat);
  right.position.set(1.35, 0.52, 0);
  const band = new THREE.Mesh(new THREE.BoxGeometry(2.7, 0.06, 0.04), mat(0xffffff));
  band.position.y = 1.0;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(2.6, 0.85),
    new THREE.MeshBasicMaterial({
      color: 0xcfd8dc,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
    }),
  );
  mesh.position.y = 0.55;
  group.add(left, right, band, mesh);
  return group;
}

export function makeHotdogStand(): THREE.Group {
  const kenney = cloneTownProp("stall", 2) ?? cloneTownProp("stall", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const cart = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.55, 0.7), mat(0xe53935));
  cart.position.y = 0.45;
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.08, 0.8), mat(0xffc107));
  top.position.y = 0.76;
  const umbrella = new THREE.Mesh(new THREE.ConeGeometry(0.7, 0.25, 8), mat(0xffeb3b));
  umbrella.position.y = 1.45;
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.7, 6), mat(0xb0bec5));
  pole.position.y = 1.1;
  const dog = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.1, 0.1), mat(0xffcc80));
  dog.position.set(0, 0.82, 0.2);
  group.add(cart, top, pole, umbrella, dog);
  return group;
}

export function makeIceCreamCart(): THREE.Group {
  const kenney = cloneTownProp("cart", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const cart = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.6, 0.7), mat(0x29b6f6));
  cart.position.y = 0.45;
  const lid = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.1, 0.75), mat(0xe1f5fe));
  lid.position.y = 0.8;
  const swirl = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.28, 8), mat(0xff80ab));
  swirl.position.set(0.2, 1.0, 0);
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.2, 6), mat(0xffcc80));
  cone.position.set(0.2, 0.88, 0);
  group.add(cart, lid, cone, swirl);
  return group;
}

export function makePretzelCart(): THREE.Group {
  const kenney = cloneTownProp("stall", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const cart = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.5, 0.65), mat(0x6d4c41));
  cart.position.y = 0.4;
  const tray = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.06, 0.55), mat(0xffe082));
  tray.position.y = 0.68;
  for (let i = 0; i < 3; i++) {
    const p = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.025, 6, 10), mat(0xd7ccc8));
    p.position.set(-0.22 + i * 0.22, 0.76, 0);
    p.rotation.x = Math.PI / 2;
    group.add(p);
  }
  group.add(cart, tray);
  return group;
}

export function makePopcornCart(): THREE.Group {
  const kenney = cloneTownProp("stall", 1) ?? cloneTownProp("cart", 1);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const cart = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 0.7), mat(0xfdd835));
  cart.position.y = 0.42;
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.45, 0.5),
    new THREE.MeshLambertMaterial({ color: 0xb3e5fc, transparent: true, opacity: 0.45 }),
  );
  glass.position.y = 0.9;
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.12, 0.72), mat(0xe53935));
  stripe.position.y = 0.55;
  group.add(cart, glass, stripe);
  return group;
}

export function makeBalloonCart(): THREE.Group {
  const kenney = cloneTownProp("cart", 1) ?? cloneTownProp("cart", 0);
  if (kenney) {
    return kenney;
  }
  const group = new THREE.Group();
  const cart = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.45, 0.6), mat(0x8e24aa));
  cart.position.y = 0.35;
  const colors = [0xef5350, 0x42a5f5, 0xffee58, 0x66bb6a, 0xff80ab];
  for (let i = 0; i < 5; i++) {
    const balloon = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), mat(colors[i]!));
    balloon.position.set(-0.28 + i * 0.14, 1.05 + (i % 2) * 0.08, 0.05);
    const string = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.55, 4), mat(0xeeeeee));
    string.position.set(balloon.position.x, 0.7, 0.05);
    group.add(balloon, string);
  }
  group.add(cart);
  return group;
}

export function makeParkSign(label = "Park"): THREE.Group {
  const group = new THREE.Group();
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 1.1, 6), mat(0x6d4c41));
  post.position.y = 0.55;
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.35, 0.08), mat(0xfff8e1));
  board.position.y = 1.15;
  group.add(post, board);
  group.userData.signLabel = label;
  return group;
}

/** Outdoor amphitheater stage - wide platform, wings, tall backdrop, soft spots. */
export function makeStage(): THREE.Group {
  const group = new THREE.Group();
  // Deck bottom clears sunk amph Floor (~local 0.14+) so volumes don't z-fight.
  const deck = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.28, 4.2), mat(0xc4a574));
  deck.position.y = 0.36;
  const lip = new THREE.Mesh(new THREE.BoxGeometry(7.4, 0.1, 0.22), mat(0xa1887f));
  lip.position.set(0, 0.46, -2.0);
  const wingL = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.22, 3.6), mat(0xbcaaa4));
  wingL.position.set(-4.0, 0.32, 0.1);
  const wingR = wingL.clone();
  wingR.position.x = 4.0;
  const back = new THREE.Mesh(new THREE.BoxGeometry(7.6, 2.6, 0.16), mat(0x6d4c41));
  back.position.set(0, 1.55, 2.05);
  const curtain = new THREE.Mesh(new THREE.BoxGeometry(6.6, 2.1, 0.08), mat(0xb71c1c));
  curtain.position.set(0, 1.5, 1.95);
  const swag = new THREE.Mesh(new THREE.BoxGeometry(6.8, 0.35, 0.1), mat(0xffd54f));
  swag.position.set(0, 2.55, 1.9);
  const stepWide = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.14, 0.55), mat(0xbcaaa4));
  stepWide.position.set(0, 0.18, -2.35);
  const stepNarrow = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.12, 0.4), mat(0xa1887f));
  stepNarrow.position.set(0, 0.12, -2.7);
  for (const x of [-2.8, -1.0, 1.0, 2.8] as const) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 2.4, 6), mat(0x78909c));
    pole.position.set(x, 1.35, 1.6);
    const cone = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.28, 8), mat(0xfff59d));
    cone.position.set(x, 2.55, 1.4);
    cone.rotation.x = 0.45;
    cone.userData.ambientGlow = "lamp";
    group.add(pole, cone);
  }
  // Side banners
  for (const x of [-3.5, 3.5] as const) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 2.8, 6), mat(0x5d4037));
    pole.position.set(x, 1.5, -1.2);
    const flag = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.1, 0.7), mat(x < 0 ? 0x1565c0 : 0x2e7d32));
    flag.position.set(x + (x < 0 ? 0.35 : -0.35), 1.9, -1.2);
    group.add(pole, flag);
  }
  group.add(deck, lip, wingL, wingR, back, curtain, swag, stepWide, stepNarrow);
  return group;
}

/** Short wooden railing post for lake bridges. */
export function makeBridgeRail(): THREE.Group {
  // Keep procedural - Kenney fence is a full yard panel and WORLD_PROP_SCALE
  // would turn "rails" into a lumber pile on the docks.
  // Span ~1 tile so adjacent pier-edge segments join into a continuous fence.
  const group = new THREE.Group();
  group.userData.skipHoverOutline = true;
  const wood = mat(0x6d4c41);
  const rail = mat(0x8d6e63);
  // Offset toward local +Z so rotY aims the rail at the water edge.
  const z = 0.42;
  const postL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.54, 0.1), wood);
  postL.position.set(-0.46, 0.29, z);
  const postR = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.54, 0.1), wood);
  postR.position.set(0.46, 0.29, z);
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.02, 0.07, 0.08), rail);
  top.position.set(0, 0.52, z);
  const mid = new THREE.Mesh(new THREE.BoxGeometry(1.02, 0.05, 0.06), mat(0xa1887f));
  mid.position.set(0, 0.33, z);
  group.add(postL, postR, top, mid);
  return group;
}

/** A-frame swing set - open underneath so pups can run through. */
export function makeSwingSet(): THREE.Group {
  const group = new THREE.Group();
  const steel = mat(0x546e7a);
  const seat = mat(0xc62828);
  const rope = mat(0x5d4037);
  for (const x of [-1.35, 1.35] as const) {
    const legA = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 2.4, 6), steel);
    legA.position.set(x, 1.15, -0.85);
    legA.rotation.x = 0.38;
    const legB = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 2.4, 6), steel);
    legB.position.set(x, 1.15, 0.85);
    legB.rotation.x = -0.38;
    group.add(legA, legB);
  }
  const top = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 3.0, 6), steel);
  top.rotation.z = Math.PI / 2;
  top.position.set(0, 2.15, 0);
  group.add(top);
  for (const sx of [-0.7, 0.7] as const) {
    for (const z of [-0.12, 0.12] as const) {
      const strand = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.35, 5), rope);
      strand.position.set(sx, 1.45, z);
      group.add(strand);
    }
    const board = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.06, 0.22), seat);
    board.position.set(sx, 0.72, 0);
    group.add(board);
  }
  return group;
}

/** Bright playground slide with a short ladder - walkable around. */
export function makeSlide(): THREE.Group {
  const group = new THREE.Group();
  const yellow = mat(0xfbc02d);
  const blue = mat(0x1e88e5);
  const tower = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.35, 1.1), blue);
  tower.position.set(-0.9, 0.7, 0);
  const railL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.55, 1.0), yellow);
  railL.position.set(-1.35, 1.55, 0);
  const railR = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.55, 1.0), yellow);
  railR.position.set(-0.45, 1.55, 0);
  const chute = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.12, 0.7), yellow);
  chute.position.set(0.55, 0.85, 0);
  chute.rotation.z = -0.42;
  const sideA = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.28, 0.08), blue);
  sideA.position.set(0.55, 0.98, 0.34);
  sideA.rotation.z = -0.42;
  const sideB = sideA.clone();
  sideB.position.z = -0.34;
  for (let i = 0; i < 4; i++) {
    const rung = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.05, 0.08), mat(0xfff59d));
    rung.position.set(-0.9, 0.35 + i * 0.28, 0.62);
    group.add(rung);
  }
  group.add(tower, railL, railR, chute, sideA, sideB);
  return group;
}

/** Low climbing cubes - pups can weave through the gaps. */
export function makeClimber(): THREE.Group {
  const group = new THREE.Group();
  const colors = [0xe53935, 0x43a047, 0x1e88e5, 0xfdd835];
  const cells: [number, number, number, number][] = [
    [-0.55, 0.35, -0.4, 0],
    [0.55, 0.55, -0.2, 1],
    [-0.2, 0.75, 0.55, 2],
    [0.65, 0.35, 0.55, 3],
  ];
  for (const [x, h, z, ci] of cells) {
    const box = new THREE.Mesh(new THREE.BoxGeometry(0.85, h, 0.85), mat(colors[ci]!));
    box.position.set(x, h * 0.5, z);
    group.add(box);
  }
  return group;
}

/** Lawn drum circle - colorful cushions only (no central pit). */
export function makeDrumCircle(): THREE.Group {
  const group = new THREE.Group();
  const cushionColors = [0xc62828, 0x1565c0, 0x2e7d32, 0x6a1b9a, 0xef6c00, 0x00838f, 0xc2185b, 0xf9a825];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const cushion = new THREE.Mesh(
      new THREE.CylinderGeometry(0.36, 0.4, 0.12, 8),
      mat(cushionColors[i]!),
    );
    cushion.position.set(Math.cos(a) * 3.1, 0.07, Math.sin(a) * 3.1);
    group.add(cushion);
  }
  return group;
}

/** Open park shelter - posts + peaked roof, walkable underneath. */
export function makePavilion(): THREE.Group {
  const group = new THREE.Group();
  const postMat = mat(0x8d6e63);
  const postGeo = new THREE.CylinderGeometry(0.07, 0.09, 1.55, 6);
  const posts: [number, number][] = [
    [-1.15, -0.95],
    [1.15, -0.95],
    [-1.15, 0.95],
    [1.15, 0.95],
  ];
  for (const [px, pz] of posts) {
    const post = new THREE.Mesh(postGeo.clone(), postMat);
    post.position.set(px, 0.78, pz);
    group.add(post);
  }
  const beamN = new THREE.Mesh(new THREE.BoxGeometry(2.5, 0.1, 0.1), postMat);
  beamN.position.set(0, 1.55, -0.95);
  const beamS = beamN.clone();
  beamS.position.z = 0.95;
  const beamW = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 2.1), postMat);
  beamW.position.set(-1.15, 1.55, 0);
  const beamE = beamW.clone();
  beamE.position.x = 1.15;
  const roof = new THREE.Mesh(new THREE.ConeGeometry(1.85, 0.55, 4), mat(0xc62828));
  roof.position.y = 1.95;
  roof.rotation.y = Math.PI / 4;
  const floor = new THREE.Mesh(
    new THREE.BoxGeometry(2.5, 0.08, 2.1),
    mat(0xbcaaa4),
  );
  // Bottom clears grass / Floor so pavilion decks don't shimmer.
  floor.position.y = 0.055;
  group.add(beamN, beamS, beamW, beamE, roof, floor);
  return group;
}

/** Charcoal kettle grill with a warm glow. */
export function makeBbqGrill(): THREE.Group {
  const group = new THREE.Group();
  const legMat = mat(0x455a64);
  for (const [lx, lz] of [
    [-0.22, -0.22],
    [0.22, -0.22],
    [-0.22, 0.22],
    [0.22, 0.22],
  ] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.45, 5), legMat);
    leg.position.set(lx, 0.22, lz);
    group.add(leg);
  }
  const bowl = new THREE.Mesh(new THREE.SphereGeometry(0.38, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), mat(0x37474f));
  bowl.position.y = 0.55;
  const lid = new THREE.Mesh(new THREE.SphereGeometry(0.36, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.48), mat(0x263238));
  lid.position.y = 0.78;
  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.02, 5, 10), mat(0x90a4ae));
  handle.position.y = 1.05;
  handle.rotation.x = Math.PI / 2;
  const coals = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.24, 0.06, 8),
    new THREE.MeshBasicMaterial({ color: 0xff6f00 }),
  );
  coals.position.y = 0.58;
  coals.userData.ambientGlow = "lamp";
  group.add(bowl, lid, handle, coals);
  return group;
}

/** Picnic table with attached benches. */
export function makePicnicTable(): THREE.Group {
  const group = new THREE.Group();
  const wood = mat(0xa1887f);
  const dark = mat(0x6d4c41);
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.08, 0.7), wood);
  top.position.y = 0.72;
  for (const sx of [-0.55, 0.55]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.68, 0.08), dark);
    leg.position.set(sx, 0.34, 0);
    group.add(leg);
  }
  for (const bz of [-0.55, 0.55]) {
    const seat = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.06, 0.28), wood);
    seat.position.set(0, 0.42, bz);
    const seatLegL = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.38, 0.06), dark);
    seatLegL.position.set(-0.5, 0.2, bz);
    const seatLegR = seatLegL.clone();
    seatLegR.position.x = 0.5;
    group.add(seat, seatLegL, seatLegR);
  }
  group.add(top);
  return group;
}

export type PropKind =
  | "tree"
  | "treeAlt"
  | "bush"
  | "rock"
  | "fence"
  | "digSpot"
  | "fetchSpot"
  | "forageSpot"
  | "begSpot"
  | "forge"
  | "bank"
  | "portal"
  | "flowers"
  | "lamp"
  | "bench"
  | "stadiumSeat"
  | "hotdogStand"
  | "iceCreamCart"
  | "pretzelCart"
  | "balloonCart"
  | "popcornCart"
  | "sign"
  | "basketHoop"
  | "tennisNet"
  | "stage"
  | "bridgeRail"
  | "drumCircle"
  | "pavilion"
  | "bbqGrill"
  | "picnicTable"
  | "frisbeeGame"
  | "picnic"
  | "lawnSprayer"
  | "swingSet"
  | "slide"
  | "climber"
  | "fishingSpot"
  | "vendor";

export function makeProp(kind: PropKind, x = 0, z = 0): THREE.Object3D {
  let model: THREE.Object3D;
  switch (kind) {
    case "tree":
      model = makeTree(0);
      break;
    case "treeAlt":
      model = makeTree(1);
      break;
    case "bush":
      model = makeBush();
      break;
    case "rock":
      model = makeRock(seed(x, z) > 0.5 ? 1 : 0);
      break;
    case "fence":
      model = makeFence();
      break;
    case "digSpot":
      model = makeDigMound();
      break;
    case "fetchSpot":
      model = makeFetchBush();
      break;
    case "forageSpot":
      model = makeForageSpot();
      break;
    case "begSpot":
      // Beg humans use a sprite bench; empty park benches use makeBench via "bench".
      model = makeBench();
      break;
    case "forge":
      model = makeForge();
      break;
    case "bank":
      model = makeVault();
      break;
    case "portal":
      model = makePortal();
      break;
    case "flowers":
      model = makeFlowerPatch(Math.floor(seed(x, z) * 3));
      break;
    case "lamp":
      model = makeLampPost();
      break;
    case "bench":
      model = makeBench();
      break;
    case "stadiumSeat":
      model = makeStadiumSeat();
      break;
    case "hotdogStand":
      model = makeHotdogStand();
      break;
    case "iceCreamCart":
      model = makeIceCreamCart();
      break;
    case "pretzelCart":
      model = makePretzelCart();
      break;
    case "balloonCart":
      model = makeBalloonCart();
      break;
    case "popcornCart":
      model = makePopcornCart();
      break;
    case "sign":
      model = makeParkSign();
      break;
    case "basketHoop":
      model = makeBasketHoop();
      break;
    case "tennisNet":
      model = makeTennisNet();
      break;
    case "stage":
      model = makeStage();
      break;
    case "bridgeRail":
      model = makeBridgeRail();
      break;
    case "drumCircle":
      model = makeDrumCircle();
      break;
    case "pavilion":
      model = makePavilion();
      break;
    case "bbqGrill":
      model = makeBbqGrill();
      break;
    case "picnicTable":
      model = makePicnicTable();
      break;
    case "frisbeeGame":
      // Marker only - kids + disc spawn in ParkFrisbee.
      model = new THREE.Group();
      break;
    case "picnic":
      // Marker only - blanket + guests spawn in ParkPicnic.
      model = new THREE.Group();
      break;
    case "lawnSprayer":
      // Marker only - stake + spray spawn in ParkLawnSprayer.
      model = new THREE.Group();
      break;
    case "swingSet":
      model = makeSwingSet();
      break;
    case "slide":
      model = makeSlide();
      break;
    case "climber":
      model = makeClimber();
      break;
    case "fishingSpot":
      // Marker only - anglers + line spawn in ParkFishing.
      model = new THREE.Group();
      break;
    case "vendor":
      // Vendor human is a billboard; keep an empty anchor for legacy callers.
      model = new THREE.Group();
      break;
    default:
      model = makeBush();
  }
  model.rotation.y = seed(x + 1.7, z + 3.1) * Math.PI * 2;
  if (kind === "fence") {
    model.rotation.y = Math.round(seed(x, z) * 4) * (Math.PI / 2);
  }
  if (
    kind === "sign" ||
    kind.endsWith("Stand") ||
    kind.endsWith("Cart") ||
    kind === "vendor" ||
    kind === "stage" ||
    kind === "bench" ||
    kind === "stadiumSeat" ||
    kind === "bridgeRail" ||
    kind === "drumCircle" ||
    kind === "pavilion" ||
    kind === "bbqGrill" ||
    kind === "picnicTable" ||
    kind === "frisbeeGame" ||
    kind === "picnic" ||
    kind === "lawnSprayer" ||
    kind === "swingSet" ||
    kind === "slide" ||
    kind === "climber" ||
    kind === "fishingSpot"
  ) {
    model.rotation.y = 0;
  }
  if (kind === "basketHoop" || kind === "tennisNet") {
    model.rotation.y = 0;
  }
  const s = 0.92 + seed(x * 2.1, z * 1.7) * 0.22;
  if (kind === "tree" || kind === "treeAlt") {
    model.scale.setScalar((1.05 + seed(x, z) * 0.28) * WORLD_PROP_SCALE);
  } else if (
    kind === "forge" ||
    kind === "bank" ||
    kind === "portal" ||
    kind === "bench" ||
    kind === "stadiumSeat" ||
    kind === "begSpot" ||
    kind === "vendor" ||
    kind === "basketHoop" ||
    kind === "tennisNet" ||
    kind === "stage" ||
    kind === "bridgeRail" ||
    kind === "drumCircle" ||
    kind === "pavilion" ||
    kind === "bbqGrill" ||
    kind === "picnicTable" ||
    kind === "frisbeeGame" ||
    kind === "picnic" ||
    kind === "lawnSprayer" ||
    kind === "swingSet" ||
    kind === "slide" ||
    kind === "climber" ||
    kind === "fishingSpot" ||
    kind.endsWith("Stand") ||
    kind.endsWith("Cart") ||
    kind === "sign"
  ) {
    model.scale.setScalar(WORLD_PROP_SCALE);
  } else {
    model.scale.setScalar(s * WORLD_PROP_SCALE);
  }
  return model;
}
